import React, { useState } from "react";
import {
  Box,
  HStack,
  Heading,
  IconButton,
  Spinner,
  Tooltip,
  VStack,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import { BsBarChartLine, BsStarFill, BsTrash } from "react-icons/bs";
import ConfirmDialog from "../common/ConfirmDialog";
import TickerSearch from "./TickerSearch";

export default function Watchlist({
  watchlist,
  selectedTicker,
  onSelectTicker,
  onAddTicker,
  onRemoveTicker,
  // When provided, each row gets a chart icon button that calls this with the
  // ticker. Used on Home where clicking should navigate to /study/<ticker>
  // rather than just selecting an active ticker on the same page.
  onChartNavigate,
  heading = "Watchlist",
  variant = "panel",
  // Optional: ticker currently being added/removed. The matching row's remove
  // button shows a spinner instead of a trash icon. Add path can be tracked
  // with `addingTicker`. Both default to null (no spinner anywhere).
  addingTicker = null,
  removingTicker = null,
}) {
  const { colorMode } = useColorMode();
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const isCard = variant === "card";
  const isAdding = Boolean(addingTicker);

  const containerProps = isCard
    ? { bg: "transparent", p: 0, minW: "100%", maxW: "100%" }
    : {
        bg: colorMode === "light" ? "gray.50" : "gray.700",
        borderRadius: "md",
        p: 3,
        h: "100%",
        minW: { base: "100%", lg: "260px" },
        maxW: { base: "100%", lg: "300px" },
      };

  return (
    <VStack align="stretch" spacing={3} {...containerProps}>
      {heading ? <Heading size="sm">{heading}</Heading> : null}
      <TickerSearch
        onSubmit={onAddTicker}
        placeholder="Add ticker (e.g. AAPL)"
        submitLabel={isAdding ? "Adding…" : "Add"}
        isSubmitting={isAdding}
      />

      {watchlist.length === 0 ? (
        <Text fontSize="xs" color="gray.500" pt={4} textAlign="center">
          No tickers yet — add one above to start tracking.
        </Text>
      ) : (
        <VStack align="stretch" spacing={1} overflowY="auto">
          {watchlist.map((row) => {
            const isSelected = row.ticker_name === selectedTicker;
            return (
              <HStack
                key={row.ticker_name}
                px={2}
                py={1}
                borderRadius="sm"
                bg={
                  isSelected
                    ? colorMode === "light"
                      ? "blue.100"
                      : "blue.700"
                    : "transparent"
                }
                _hover={{
                  bg: colorMode === "light" ? "blue.50" : "blue.800",
                  cursor: onSelectTicker ? "pointer" : "default",
                }}
                onClick={() => onSelectTicker?.(row.ticker_name)}
              >
                <BsStarFill color="#F6AD55" />
                <Text fontWeight={isSelected ? "bold" : "normal"} flex="1">
                  {row.ticker_name}
                </Text>
                {onChartNavigate ? (
                  <Tooltip label="Go to chart and notes" hasArrow fontSize="xs">
                    <IconButton
                      size="xs"
                      aria-label={`Go to ${row.ticker_name} chart`}
                      icon={<BsBarChartLine />}
                      variant="ghost"
                      colorScheme="blue"
                      onClick={(e) => {
                        e.stopPropagation();
                        onChartNavigate(row.ticker_name);
                      }}
                    />
                  </Tooltip>
                ) : null}
                <Tooltip label="Remove from watchlist" hasArrow fontSize="xs">
                  <IconButton
                    size="xs"
                    aria-label={`Remove ${row.ticker_name}`}
                    icon={
                      removingTicker === row.ticker_name ? (
                        <Spinner size="xs" />
                      ) : (
                        <BsTrash />
                      )
                    }
                    variant="ghost"
                    isDisabled={removingTicker === row.ticker_name}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingRemoval(row.ticker_name);
                    }}
                  />
                </Tooltip>
              </HStack>
            );
          })}
        </VStack>
      )}

      <ConfirmDialog
        isOpen={Boolean(pendingRemoval)}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) onRemoveTicker(pendingRemoval);
          setPendingRemoval(null);
        }}
        title="Remove from watchlist"
        confirmLabel="Remove"
        confirmColorScheme="red"
      >
        <Box>Remove {pendingRemoval} from your watchlist?</Box>
      </ConfirmDialog>
    </VStack>
  );
}
